import { format } from "date-fns";
import { de } from "date-fns/locale";

/** Format a date range as a compact German string. */
export function formatEventDateRange(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${format(start, "dd.", { locale: de })}–${format(end, "dd. MMM yyyy", { locale: de })}`;
  } else if (sameYear) {
    return `${format(start, "dd. MMM", { locale: de })} – ${format(end, "dd. MMM yyyy", { locale: de })}`;
  } else {
    return `${format(start, "dd. MMM yyyy", { locale: de })} – ${format(end, "dd. MMM yyyy", { locale: de })}`;
  }
}

export const formatDateTime = (date: Date) =>
  new Intl.DateTimeFormat("de-DE", {
    dateStyle: "long",
    timeStyle: "short",
    timeZone: "Europe/Berlin",
  }).format(date);

export const formatDate = (date: Date) =>
  new Intl.DateTimeFormat("de-DE", {
    dateStyle: "short",
  }).format(date);

const pad = (n: number) => `${Math.floor(Math.abs(n))}`.padStart(2, "0");

/* Returns date in format YYYY-MM-DDTHH:MM, suitable for input types of dateinput-local */
export const formatISO = (date: Date) => {
  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes())
  );
};

/** Returns today's date at 00:00 as an ISO string — used to keep events visible for their entire day. */
export const startOfDayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

/** Add months safely, clamping to end-of-month (e.g. Jan 31 + 1 = Feb 28, not Mar 3). */
export function addMonthsSafe(date: Date, months: number): Date {
  const result = new Date(date);
  const targetMonth = result.getMonth() + months;
  result.setMonth(targetMonth);
  if (result.getMonth() !== ((targetMonth % 12) + 12) % 12) {
    result.setDate(0);
  }
  return result;
}

export const relativeTimeString = (date: Date | number): string => {
  // Allow dates or times to be passed
  const timeMs = typeof date === "number" ? date : date.getTime();

  // Get the amount of seconds between the given date and now
  const deltaSeconds = Math.round((timeMs - Date.now()) / 1000);

  // Array reprsenting one minute, hour, day, week, month, etc in seconds
  const cutoffs = [
    60,
    3600,
    86400,
    86400 * 7,
    86400 * 30,
    86400 * 365,
    Infinity,
  ];

  // Array equivalent to the above but in the string representation of the units
  const units: Intl.RelativeTimeFormatUnit[] = [
    "second",
    "minute",
    "hour",
    "day",
    "week",
    "month",
    "year",
  ];

  // Grab the ideal cutoff unit
  const unitIndex = cutoffs.findIndex(
    (cutoff) => cutoff > Math.abs(deltaSeconds)
  );

  // Get the divisor to divide from the seconds. E.g. if our unit is "day" our divisor
  // is one day in seconds, so we can divide our seconds by this to get the # of days
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;

  if (!deltaSeconds || !divisor) {
    return "";
  }

  // Intl.RelativeTimeFormat do its magic
  const rtf = new Intl.RelativeTimeFormat("de-DE", { numeric: "auto" });
  return rtf.format(Math.floor(deltaSeconds / divisor), units[unitIndex]);
};
